import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, inject, OnDestroy, OnInit, Signal, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { NoDataComponent } from 'projects/ui-library/src/lib/components/no-data/no-data.component';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, defaultIfEmpty, map, take, takeUntil } from 'rxjs/operators';
import { Pop } from 'src/app/classes/shared/pop';
import { Tools } from 'src/app/classes/shared/tools';
import { Project } from 'src/app/interfaces/api/project';
import { Tracker } from 'src/app/interfaces/api/tracker';
import { User } from 'src/app/interfaces/api/user';
import { TimeFormatPipe } from 'src/app/pipes/time-format.pipe';
import { InterventionService } from 'src/app/services/api/interventions/intervention.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { TrackerService } from 'src/app/services/api/trackers/tracker.service';
import { UserService } from 'src/app/services/api/users/user.service';
import { AuthService } from 'src/app/services/core/auth.service';
import { ButtonComponent, ChipComponent, SearchBarComponent, SpinnerComponent } from 'ui-library';
import { EditTrackerModal } from './edit-tracker-modal/edit-tracker-modal.component';
import { MapComponent } from './map/map.component';
import { MergeTrackersModal } from './merge-trackers-modal/merge-trackers-modal.component';
import { SetConfigModalComponent } from './set-config-modal/set-config-modal.component';
import { SetProjectModalComponent } from './set-project-modal/set-project-modal.component';

@Component({
  selector: 'app-devices',
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    MatSlideToggleModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCheckboxModule,
    MatPaginatorModule,
    MatTableModule,
    MatIconModule,
    MatSortModule,
    MatPaginatorModule,
    MatChipsModule,
    MatMenuModule,
    MatToolbarModule,
    TimeFormatPipe,
    MapComponent,
    ButtonComponent,
    SearchBarComponent,
    SpinnerComponent,
    ChipComponent,
    NoDataComponent
  ],
  templateUrl: './devices.component.html',
  styleUrls: ['./devices.component.scss'],
  standalone: true
})

export class DevicesComponent implements AfterViewInit, OnInit, OnDestroy {
  loading: Boolean = false;
  @ViewChild('devicePaginator', { static: false }) devicePaginator!: MatPaginator;
  @ViewChild(MatSort) sort: MatSort = new MatSort();
  user!: User;
  devices: Tracker[] = [];
  devicesCount: Number = 0;
  devicesData: MatTableDataSource<Tracker> = new MatTableDataSource();
  devicesColumns: string[] = ['select', 'name', 'projects', 'status', 'actions'];
  selectedDevices: any[] = [];
  lastVisitedProjects: string[] = [];
  searchText: string = "";
  validSearchText: string = "";
  trackerConfigs: any[] = [];
  mapDisplayed: boolean = true;
  canMerge = false;
  destroy$: Subject<void> = new Subject<void>();
  searchTerms: Subject<string> = new Subject<string>();
  requestCancel$ = new Subject<void>();
  readonly canEditConfigs: Signal<boolean> = inject(AuthService).canManageProject;

  constructor(
    private trackerService: TrackerService,
    private userService: UserService,
    private auth: AuthService,
    private dialog: MatDialog,
    private tools: Tools,
    private pop: Pop,
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private projectService: ProjectService,
    private interventionService: InterventionService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.getConfigs();
    this.resumeTrackerDeepLinkIfAny();
  }

  // Counterpart of EditTrackerModal.addInterventionForTracker(): that method redirects to
  // Interventions with `?tracker=<uuid>&returnTo=devices` (exact same mechanism ResourcesComponent
  // already uses for its own Dispositif side panel) - InterventionsComponent then redirects back
  // here with `?tracker=<uuid>` once the drawer closes/saves, and this reopens EditTrackerModal
  // for that same device so linking an intervention doesn't just strand the user on the plain
  // devices list.
  private resumeTrackerDeepLinkIfAny(): void {
    const uuid = this.route.snapshot.queryParamMap.get('tracker');
    if (!uuid) return;

    // One-shot: clears the query param immediately so a manual refresh of this exact URL later
    // doesn't keep reopening the dialog.
    this.router.navigate([], { queryParams: { tracker: null }, queryParamsHandling: 'merge', replaceUrl: true });

    this.trackerService.getMasterTrackerByUuid(uuid).pipe(takeUntil(this.destroy$)).subscribe({
      next: (tracker: any) => {
        if (!tracker) return;
        // EditTrackerModal reads the Mongo id off `_id` (see its own ngOnInit()) - the
        // master/uuid lookup's response isn't guaranteed to carry that exact field itself.
        this.editDevice({ ...tracker, _id: tracker.id });
      },
      error: (err: any) => console.error('[Devices] Failed to reload tracker from intervention deep-link:', err),
    });
  }

  ngAfterViewInit(): void {
    this.user = this.auth.currentUser();
    this.lastVisitedProjects = this.user.projectsVisited;

    Promise.resolve().then(() => {
      this.loadTrackers();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.requestCancel$.next();
    this.requestCancel$.complete();
  }

  search(text: string = "") {
    this.canMerge = false;

    if (text !== this.validSearchText) {
      this.validSearchText = text;
      this.devicePaginator.pageIndex = 0;
      this.loadTrackers();
    }
  }

  loadTrackers() {
    this.requestCancel$.next();
    this.loading = true;

    const query: any = {
      owner: this.user.id,
      author: this.user.id,
      users: this.user.id,
      offset: this.devicePaginator ? this.devicePaginator.pageIndex * this.devicePaginator.pageSize : 0,
      limit: this.devicePaginator ? this.devicePaginator.pageSize : 10
    };

    const searchText = this.validSearchText && this.validSearchText.trim() !== '' ? this.validSearchText.trim() : "";

    this.trackerService.searchAndCountTrackers(searchText, query)
      .pipe(takeUntil(this.requestCancel$))
      .subscribe({
        next: (res: any) => {
          this.handleDevices(res.results);
          this.devicesCount = res.total;

          if (this.devicePaginator)
            this.devicePaginator.length = res.total;
        },
        error: (err) => {
          this.loading = false;
          console.error('❌ Erreur lors de la recherche des trackers:', err);
        }
      });
  }

  getConfigs() {
    this.trackerService.getConfigs().pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        if (res) this.trackerConfigs = res;
      }
    })
  }

  handleDevices(devices: Tracker[]) {
    devices.forEach(t => {
      t.projects = t.projects.filter(p => p) as Project[];
      let isDelayOk = this.tools.isWithinOfflineDelay(t, this.trackerConfigs)
      t.status = isDelayOk ? "active" : "inactive";
    });

    if (this.user.role.name != "dev" && this.user.role.name != "admin") {
      let mappedDevices: any = {};

      devices.forEach(t => {
        let key = `${t.name}-${t.project}`;
        if (!mappedDevices[key])
          mappedDevices[key] = [{ id: t.id, lastUpdate: t.lastUpdate, status: t.status }];
        else
          mappedDevices[key].push({ id: t.id, lastUpdate: t.lastUpdate, status: t.status });
      });

      Object.keys(mappedDevices).forEach(key => {
        let sameNameDevices = mappedDevices[key];

        if (sameNameDevices.length == 1) return;

        sameNameDevices.sort((a: Tracker, b: Tracker) => moment(a.lastUpdate).diff(b.lastUpdate) ? -1 : 1);

        sameNameDevices.forEach((duplicate: Tracker, i: number) => {
          if (i > 0 && duplicate.status != "active") {
            let duplicateIndex = devices.findIndex(t => t.id == duplicate.id);
            devices.splice(duplicateIndex, 1);
          }
        })
      });
    }

    this.devices = devices;
    this.devices.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

    if (!this.devicesData)
      this.devicesData = new MatTableDataSource(this.devices);
    else
      this.devicesData.data = this.devices;

    this.loading = false;
    this.loadInterventionIndicators(this.devices);
  }

  // Whether each currently-displayed device has at least one linked intervention (see
  // EditTrackerModal's own "Interventions liées" section) - purely a per-row indicator, not
  // wired into any other logic here. Bounded to the current page (at most one page's worth of
  // parallel requests, refreshed whenever it changes), same reasoning as bulkDeleteDevices()'s
  // own forkJoin over the current selection.
  private loadInterventionIndicators(devices: Tracker[]): void {
    const withUuid = devices.filter(t => t.uuid);
    if (!withUuid.length) return;

    forkJoin(
      withUuid.map(t => this.interventionService.searchAndCountInterventions({ trackers: t.uuid, limit: 1 }).pipe(
        map(res => ({ uuid: t.uuid, hasInterventions: (res?.total ?? 0) > 0 })),
        catchError(() => of({ uuid: t.uuid, hasInterventions: false })),
      ))
    ).pipe(takeUntil(this.requestCancel$)).subscribe(results => {
      const linkedUuids = new Set(results.filter(r => r.hasInterventions).map(r => r.uuid));
      this.devices.forEach(t => (t as any).hasLinkedInterventions = linkedUuids.has(t.uuid));
      this.devicesData.data = [...this.devices];
    });
  }

  onRowSelected() {
    this.selectedDevices = this.devices.filter((t: any) => t.selected).map(t => t)
    this.canMerge = false;

    if ((this.user.role.name == "dev" || this.user.role.name == "admin") && this.selectedDevices.length == 2) {
      this.canMerge = true;
      let trackerName = this.selectedDevices[0].name;

      this.selectedDevices.forEach(t => {
        if (t.name != trackerName)
          this.canMerge = false;
      });
    }
  }

  // Browse/manage every TrackerConfig template, all device types included (KYD too, now that
  // it has its own templates - see tracker-config.tool.ts) - a full page (TrackerConfigsComponent),
  // not a dialog: filtering/sorting/multi-select/detail/bulk actions need the room.
  editDevicesConfig() {
    if (!this.canEditConfigs()) return;

    this.router.navigate(['/devices/configs']);
  }

  setDevicesConfig() {
    let trackers = this.devicesData.data.filter(t => t.selected)
    const modal = this.dialog.open(SetConfigModalComponent, {
      data: { trackers },
      width: '300px',
      restoreFocus: false
    })

    modal.afterClosed().subscribe((res) => {
      setTimeout(() => {
        this.loadTrackers();
      }, 1000)
    })
  }

  setDevicesProject() {
    let trackers = this.devicesData.data.filter(t => t.selected)

    const modal = this.dialog.open(SetProjectModalComponent, {
      data: { trackers },
      width: '300px',
      restoreFocus: false
    })

    modal.afterClosed().subscribe((res) => {
      setTimeout(() => {
        this.loadTrackers();
      }, 1000)
    })
  }

  selectCurrentProject(projectId: string) {
    this.userService.setCurrentProject(projectId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (user: User) => {
        if (!user) return;

        this.projectService.getProjectById(projectId).pipe(takeUntil(this.destroy$)).subscribe({
          next: (project: Project) => {
            if (!project) {
              console.error("Project not found");
              return;
            }

            this.projectService.getStatus(project.database).pipe(takeUntil(this.destroy$)).subscribe({
              next: (result: any) => {
                project.status = result[0];
                const index = this.lastVisitedProjects.indexOf(projectId);

                if (index > -1) this.lastVisitedProjects.splice(index, 1);

                this.lastVisitedProjects.push(projectId);
                this.userService.addVisitedProjects(this.lastVisitedProjects);
                this.setCurrentProject(project);
                this.router.navigate(['/project/project-settings/resources'], { queryParams: { tab: 1 } });
              }
            });
          }
        });
      },
      error: (err) => {
        console.error(err);
      }
    })


    this.projectService.getProjectById(projectId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (project: Project) => {
        if (!project) {
          console.error("Project not found");
          return;
        }

        this.projectService.getStatus(project.database).pipe(takeUntil(this.destroy$)).subscribe({
          next: (result: any) => {
            project.status = result[0];
            this.projectService.setCurrentProject(project);
            const index = this.lastVisitedProjects.indexOf(projectId);

            if (index > -1)
              this.lastVisitedProjects.splice(index, 1);

            this.lastVisitedProjects.push(projectId);
            this.userService.addVisitedProjects(this.lastVisitedProjects);
            this.router.navigate(['/resources'])
          }
        });
      }
    });
  }

  setCurrentProject(project: Project) {
    this.projectService.getActivity({ databases: project.database }).pipe(takeUntil(this.destroy$))
      .pipe(take(1), defaultIfEmpty(null)).subscribe({
        next: (result: any) => {
          if (result) {
            project.activity = result;
          }

          this.projectService.setCurrentProject(project);
        },
        error: (err: any) => {
          console.error('Error getting project activity:', err);
          this.projectService.setCurrentProject(project);
        }
      })
  }

  editDevice(tracker = null) {
    const modal = this.dialog.open(EditTrackerModal, {
      width: '800px',
      height: '70%',
      data: { tracker, user: this.user, title: tracker ? undefined : 'trackers.addTracker' }
    });

    modal.afterClosed().subscribe(res => {
      this.loadTrackers();
    })
  }

  confirmDeleteTracker() {
    let trackers = this.devicesData.data.filter(t => t.selected);

    this.pop.confirm(this.translate.instant('trackers.confirm_delete_trackers'), (confirm: any) => {
      if (confirm.ok) {
        this.trackerService.deleteTrackers({ uuids: trackers.map(t => t.uuid) }).pipe(takeUntil(this.destroy$))
          .pipe(take(1), defaultIfEmpty(null)).subscribe({
            next: (res: any) => {
              this.pop.toast(this.translate.instant('trackers.success.removed_trackers'), 'success', 4000, "delete");
              this.loadTrackers();
            },
            error: (err: any) => {
              if (err == 'offline') {
                this.pop.warning(this.translate.instant('units.impossible_you_are_offline'));
              } else {
                this.pop.error(this.translate.instant('trackers.error.removing_trackers'));
              }

              console.error('Error deleting trackers:', err);
            }
          });
      }
    });
  }

  toggleMap($event: any): void {
    this.mapDisplayed = $event.checked;
  }

  handlePageEvent(e: PageEvent) {
    this.loadTrackers();
  }

  mergeTrackers(trackers: Tracker[] = []) {
    const modal = this.dialog.open(MergeTrackersModal, {
      width: '800px',
      data: { trackers, user: this.user }
    });

    modal.afterClosed().subscribe(res => {
      if (res)
        this.loadTrackers();
    })
  }
}
